/** The approved recording return uses the native Resource journal. The carry
 * holds only its entry locator and the fully admitted recording pair. */
import { captureRecapBoundary, getCoachResourceRecording, getCoachResourceRevision,
  previewCoachingVideoReference } from '@/lib/api';
import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import { completeResourceEditorLink, initializeResourceEditorContinuation, loadResourceEditorContinuation } from '@/lib/resourceEditorContinuation';
import { createResourceVideoSession, emptyResourceVideo, type ResourceVideoSession } from '@/features/resource-editor/resourceVideoSession';
import type { DrawingV2 } from '@/lib/canvas/telestrator-schema';
import type { ResourceCommandResult, ResourceRecordingDescriptor } from '@/lib/types';
import { decodePrivateFeedbackReviewCarry, type PrivateFeedbackReviewCarry } from './privateFeedbackReview';
import type { ReviewOrigin } from './reviewState';
import { decodeCanonicalFeedbackReviewCarry, type CanonicalFeedbackReviewCarry } from './canonicalFeedbackReview';

export type PrivateRecordingTarget = { origin: ReviewOrigin; privateFeedback: PrivateFeedbackReviewCarry };
export type PrivateRecordingSelection = { matchId: string; slot: ResourceRecordingDescriptor['slot'] };
export type PrivateRecordingSnapshot = PrivateRecordingSelection & { timestampSeconds: number; drawing: DrawingV2 };
const sameOrigin = (a: ReviewOrigin, b: ReviewOrigin) => a.captureId === b.captureId && a.draftId === b.draftId
  && a.draftRevision === b.draftRevision && a.confirmationId === b.confirmationId
  && a.confirmationRevision === b.confirmationRevision;
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const clone = <T>(value: T): T => structuredClone(value);
const sameId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

export function createPrivateRecordingReturn(owner: DraftContext, origin: ReviewOrigin, access: {
  read(): PrivateRecordingTarget | null;
  /** Merge into the latest full carry; true requires successful exact readback. */
  write(value: PrivateFeedbackReviewCarry): boolean;
  active(): boolean;
}) {
  return createAssistRecordingReturn(owner, origin, 'private', {
    read: () => { const value = access.read(); return value && { origin: value.origin, writing: value.privateFeedback }; },
    write: access.write, active: access.active,
  });
}

export function createPublicRecordingReturn(owner: DraftContext, origin: ReviewOrigin, access: {
  read(): { origin: ReviewOrigin; canonicalFeedback: CanonicalFeedbackReviewCarry } | null;
  write(value: CanonicalFeedbackReviewCarry): boolean;
  active(): boolean;
}) {
  return createAssistRecordingReturn(owner, origin, 'public', {
    read: () => { const value = access.read(); return value && { origin: value.origin, writing: value.canonicalFeedback }; },
    write: access.write, active: access.active,
  });
}

/** Both approved flows use the same independent Resource operation. The family
 * is part of the captured return target, never inferred from the saved prose. */
function createAssistRecordingReturn(owner: DraftContext, origin: ReviewOrigin, family: 'private' | 'public', access: {
  read(): { origin: ReviewOrigin; writing: CanonicalFeedbackReviewCarry } | null;
  write(value: CanonicalFeedbackReviewCarry): boolean;
  active(): boolean;
}) {
  const scope = Object.freeze({ ...owner }), captured = clone(origin);
  const target = { kind: 'assist' as const, origin: captured, ...(family === 'public' ? { approval_family: 'public' as const } : {}) };
  const decode = family === 'private' ? decodePrivateFeedbackReviewCarry : decodeCanonicalFeedbackReviewCarry;
  const boundary = captureRecapBoundary({ ...scope, view: 'coach' });
  const abort = new AbortController();
  let disposed = false, busy = false, session: ResourceVideoSession | null = null;
  let receipt: ResourceCommandResult | null = null;
  function current() {
    if (disposed || abort.signal.aborted || !boundary.current() || !isCurrentDraftContext(scope) || !access.active())
      throw new DOMException('Recording return context changed', 'AbortError');
    const value = access.read();
    if (!value || !sameOrigin(value.origin, captured) || !decode(value.writing))
      throw new Error('Original Assist review unavailable. Your independent Resource is kept.');
    return value.writing;
  }
  function write(value: PrivateFeedbackReviewCarry) {
    current();
    if (!access.write(clone(value)) || !same(current(), value))
      throw new Error('Your writing could not be kept. Stay here and retry the original return.');
  }
  function originalSelection(selection: PrivateRecordingSelection) {
    const state = session!.getState();
    if (state.status !== 'available' || !state.value.recording)
      throw new Error('Original saved recording is unavailable. Your Resource is kept.');
    if (state.value.recording.match_id !== selection.matchId || state.value.recording.slot !== selection.slot)
      throw new Error('Return to the original recording and slot to finish this saved Resource return.');
    return state.value;
  }
  return {
    getState: () => {
      const available = !disposed && boundary.current() && isCurrentDraftContext(scope) && access.active();
      const state = available ? session?.getState() : undefined;
      return { busy, receipt: available && receipt ? clone(receipt) : null,
        rejected: state?.status === 'available' && state.command?.status === 'rejected' };
    },
    async run(selection: PrivateRecordingSelection, snapshot: () => PrivateRecordingSnapshot,
      options: { repairRejected?: boolean; isCurrent?: () => boolean } = {}) {
      const assertRun = () => {
        const value = current();
        if (options.isCurrent?.() === false) throw new DOMException('Recording selection changed', 'AbortError');
        return value;
      };
      assertRun();
      if (busy) throw new Error('The original recording return is still running.');
      busy = true;
      try {
        let carried = assertRun();
        // Both the empty exact-target journal and locator precede source work.
        // It never contains or replaces the native journal's command/receipt.
        if (!carried.recording_entry_id) {
          const allocated = crypto.randomUUID();
          initializeResourceEditorContinuation(scope, allocated, target, 'moment');
          write({ ...carried, recording_entry_id: allocated });
          carried = assertRun();
        }
        const entryId = carried.recording_entry_id!;
        if (session && session.entryId !== entryId) throw new Error('Original Resource entry changed.');
        const previousPair = clone(carried.recording);
        const kept = loadResourceEditorContinuation(scope, entryId);
        if (!kept) throw new Error('Original Resource journal is unavailable. No new save was sent.');
        if (kept.return_target.kind !== 'assist' || !sameOrigin(kept.return_target.origin, captured)
            || kept.return_target.approval_family !== target.approval_family)
          throw new Error('Resource return belongs to a different review.');
        if (!session) {
          session = createResourceVideoSession(scope, entryId, 'moment',
            { kind: 'new', value: emptyResourceVideo('moment') }, target);
        }
        let state = session.getState();
        if (state.status !== 'available') throw new Error('Original Resource operation is unavailable.');
        if (options.repairRejected) {
          if (state.command?.status !== 'rejected') throw new Error('The original Resource result must be checked first.');
          // Only an explicit user repair of this mounted, proved rejection.
          // Restored uncertainty never acquires this permission from a later error.
          session.releaseRejected(); state = session.getState();
          if (state.status !== 'available') throw new Error('Original Resource operation is unavailable.');
        }
        receipt = state.receipt;
        if (!state.command && !state.receipt) {
          const actual = clone(snapshot());
          if (actual.matchId !== selection.matchId || actual.slot !== selection.slot)
            throw new Error('Recording selection changed.');
          const descriptor = await getCoachResourceRecording(actual.matchId, actual.slot, { scope, signal: abort.signal });
          assertRun();
          if (descriptor.schema_version !== 'resource-recording.v1' || descriptor.match_id !== actual.matchId || descriptor.slot !== actual.slot)
            throw new Error('Recording descriptor does not match the selected source.');
          session.change({ ...emptyResourceVideo('moment'), recording: descriptor,
            drawing: actual.drawing, timestampSeconds: actual.timestampSeconds });
          receipt = await session.save();
        } else if (state.command) {
          originalSelection(selection);
          receipt = await session.retry();
        }
        assertRun();
        const original = originalSelection(selection), saved = receipt;
        if (!saved || !original.recording) throw new Error('Original Resource save is not confirmed.');
        // A receipt is historical acceptance, never fresh access or source proof.
        const detail = await getCoachResourceRevision(saved, { scope, signal: abort.signal });
        assertRun();
        const resource = detail.resource, source = original.recording;
        if (!sameId(resource.resource_id, saved.resource_id) || !sameId(resource.revision_id, saved.revision_id)
            || resource.availability !== 'available' || resource.archived || resource.kind !== 'moment'
            || resource.payload.recording_id !== source.recording_id
            || resource.payload.recording_generation !== source.recording_generation
            || resource.payload.match_id !== source.match_id || resource.payload.slot !== source.slot
            || resource.payload.timestamp_seconds !== original.timestampSeconds)
          throw new Error('Resource saved; its exact recording is unavailable for this return.');
        const reference = await previewCoachingVideoReference(captured.draftId, {
          match_id: source.match_id, slot: source.slot, timestamp_seconds: original.timestampSeconds,
          expected_draft_revision: captured.draftRevision, confirmation_id: captured.confirmationId,
          expected_confirmation_revision: captured.confirmationRevision,
        }, { teamId: scope.teamId, seasonId: scope.seasonId }, abort.signal);
        assertRun();
        const liveSource = await getCoachResourceRecording(source.match_id, source.slot, { scope, signal: abort.signal });
        assertRun();
        if (liveSource.schema_version !== source.schema_version || liveSource.recording_id !== source.recording_id
            || liveSource.recording_generation !== source.recording_generation || liveSource.match_id !== source.match_id
            || liveSource.slot !== source.slot || reference.match_id !== source.match_id || reference.slot !== source.slot
            || reference.timestamp_seconds !== original.timestampSeconds)
          throw new Error('Resource saved; recording source changed. Return to review without replacing its evidence.');
        const latest = assertRun();
        if (latest.recording_entry_id !== entryId || !same(latest.recording, previousPair))
          throw new Error('Recording choice changed. Your saved Resource and current writing are kept.');
        const links = latest.content.resource_links ?? [];
        const existing = links.find(pin => sameId(pin.resource_id, saved.resource_id));
        if (existing && !sameId(existing.revision_id, saved.revision_id))
          throw new Error('A different Resource revision is selected; explicit replacement is required.');
        if (!existing && links.length >= 10) throw new Error('Resource saved; this Feedback already has ten Resources.');
        const pin = { resource_id: saved.resource_id, revision_id: saved.revision_id };
        const next = decode({ ...latest,
          content: { ...latest.content, resource_links: existing ? links : [...links, pin] },
          recording: { video_reference: reference, recording_resource: pin },
        });
        if (!next) throw new Error('Resource saved; the recording proof could not be confirmed.');
        write(next);
        completeResourceEditorLink(scope, entryId, target, saved);
        return { receipt: clone(saved), recording: clone(next.recording!) };
      } finally { busy = false; }
    },
    dispose() { disposed = true; abort.abort(); boundary.dispose(); session?.dispose(); },
  };
}
